import { useAuthState } from '../../services/auth';
import { useEffect, useState } from 'react';
import './webfilters.scss';
import axios from 'axios';
import { Globe, Inbox, LucideIcon, Plus, ShieldBan, ShieldCheck, X } from 'lucide-react';
import '../../design-system/index.css';
import { useToast } from '../../services/ToastContext';

type Props = {
  darkmode?: boolean;
};

export const WebFilters = ({ darkmode }: Props) => {
  const { token } = useAuthState();
  const [blacklist, setBlacklist] = useState<any['No data']>();
  const [whitelist, setWhitelist] = useState<any['No data']>();
  const [errorBlacklist, setErrorBlacklist] = useState(false);
  const [errorWhitelist, setErrorWhitelist] = useState(false);
  const { showToastMessage } = useToast();

  const [currentTypedBlacklist, setCurrentTypedBlacklist] = useState<string>('');
  const [currentTypedWhitelist, setCurrentTypedWhitelist] = useState<string>('');

  useEffect(() => {
    if (token) {
      fetchApiBlacklist();
      fetchApiWhitelist();
    }
  }, [token]);

  async function fetchApiBlacklist() {
    const data = await axios.get('/op/filters/blacklist');
    setBlacklist(data?.data?.blacklist?.length ? data?.data?.blacklist : ['No data']);
  }

  async function fetchApiWhitelist() {
    const data = await axios.get('/op/filters/whitelist');
    setWhitelist(data?.data?.whitelist?.length ? data?.data?.whitelist : ['No data']);
  }

  async function addToBlacklist() {
    if (validateUrl(currentTypedBlacklist)) {
      setErrorBlacklist(false);
      const resp = await axios.patch('/op/filters/blacklist', { domains: [extractHostname(currentTypedBlacklist)] });
      setCurrentTypedBlacklist('');
      showToastMessage('Added successfully!');
      fetchApiBlacklist();
    } else {
      setErrorBlacklist(true);
    }
  }

  async function addToWhitelist() {
    if (validateUrl(currentTypedWhitelist)) {
      setErrorWhitelist(false);
      const resp = await axios.patch('/op/filters/whitelist', { domains: [extractHostname(currentTypedWhitelist)] });
      setCurrentTypedWhitelist('');
      showToastMessage('Added successfully!');
      fetchApiWhitelist();
    } else {
      setErrorWhitelist(true);
    }
  }

  async function removeFromWhitelist(domain: string) {
    const resp = await axios.delete('/op/filters/whitelist', {
      data: { domains: [domain] },
    });

    showToastMessage('Removed successfully!');
    fetchApiWhitelist();
  }

  async function removeFromBlacklist(domain: string) {
    const resp = await axios.delete('/op/filters/blacklist', {
      data: { domains: [domain] },
    });
    showToastMessage('Removed successfully!');
    fetchApiBlacklist();
  }

  function validateUrl(url: string) {
    if (!url) return false;

    if (!url.startsWith('http://') || !url.startsWith('https://')) {
      url = url.replace('www.', '');
      url += 'http://' + url;
    }

    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  }

  function extractHostname(url: string) {
    let hostname = '';
    if (url.indexOf('//') > -1) {
      hostname = url.split('/')[2];
    } else {
      hostname = url.split('/')[0];
    }
    hostname = hostname.split(':')[0];
    hostname = hostname.split('?')[0];

    return hostname.replace('www.', '');
  }

  function updateCurrentTypedBlacklist(event: any) {
    const value = event.target.value;
    setCurrentTypedBlacklist(value);
  }

  function updateCurrentTypedWhitelist(event: any) {
    const value = event.target.value;
    setCurrentTypedWhitelist(value);
  }

  // The API layer stores an empty list as ['No data']
  const realDomains = (list?: string[]) => (list || []).filter((item) => item && item !== 'No data');

  const renderPanel = (config: {
    tone: 'block' | 'allow';
    icon: LucideIcon;
    title: string;
    description: string;
    listTitle: string;
    emptyText: string;
    value: string;
    onChange: (event: any) => void;
    onAdd: () => void;
    error: boolean;
    domains: string[];
    onRemove: (domain: string) => void;
  }) => {
    const Icon = config.icon;
    return (
      <div className={`bd-card wf-panel ${config.tone}`}>
        <div className="wf-panel-hd">
          <span className="wf-panel-icon">
            <Icon size={18} strokeWidth={1.9} />
          </span>
          <div className="wf-panel-title">
            <h3 className="bd-h3">{config.title}</h3>
            <span className="bd-meta">{config.description}</span>
          </div>
          <span className={`bd-chip ${config.tone === 'block' ? 'danger' : 'mint'}`}>
            <span className="bd-mono">{config.domains.length}</span>
            {config.domains.length === 1 ? 'site' : 'sites'}
          </span>
        </div>

        <div className="wf-add">
          <label className={`bd-search wf-input ${config.error ? 'invalid' : ''}`}>
            <Globe size={14} />
            <input
              value={config.value}
              onChange={config.onChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') config.onAdd();
              }}
              placeholder="https://www..."
              aria-label={`Add to ${config.title.toLowerCase()}`}
              aria-invalid={config.error}
            />
          </label>
          <button
            type="button"
            className={`bd-btn ${config.tone === 'block' ? 'danger-soft' : 'primary'} wf-add-btn`}
            onClick={config.onAdd}
            disabled={!config.value.trim()}
          >
            <Plus size={15} strokeWidth={2.4} />
            {config.tone === 'block' ? 'Block' : 'Allow'}
          </button>
        </div>
        {config.error && <div className="wf-error">Invalid URL — enter a domain such as example.com</div>}

        <div className="wf-list-hd">
          <span className="bd-field-label">{config.listTitle}</span>
        </div>
        {config.domains.length === 0 ? (
          <div className="wf-empty">
            <Inbox size={22} strokeWidth={1.5} />
            {config.emptyText}
          </div>
        ) : (
          <ul className="wf-list">
            {config.domains.map((domain) => (
              <li key={domain} className="wf-item">
                <span className="wf-item-dot" aria-hidden="true" />
                <span className="wf-item-domain bd-mono" title={domain}>
                  {domain}
                </span>
                <button
                  type="button"
                  className="bd-iconbtn wf-remove"
                  onClick={() => config.onRemove(domain)}
                  aria-label={`Remove ${domain}`}
                  title="Remove"
                >
                  <X size={14} strokeWidth={2.2} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <section id="web-filters" className="bd wf" data-theme={darkmode ? 'dark' : 'light'} data-accent="mint">
      <div className="wf-grid">
        {renderPanel({
          tone: 'block',
          icon: ShieldBan,
          title: 'Globally Blocked Sites',
          description: 'Please enter the websites that you want to block on all devices.',
          listTitle: 'Blocked sites',
          emptyText: 'No sites are blocked globally',
          value: currentTypedBlacklist,
          onChange: updateCurrentTypedBlacklist,
          onAdd: addToBlacklist,
          error: errorBlacklist,
          domains: realDomains(blacklist),
          onRemove: removeFromBlacklist,
        })}
        {renderPanel({
          tone: 'allow',
          icon: ShieldCheck,
          title: 'Globally Allowed Sites',
          description: 'Please enter the websites that you want to allow on all devices.',
          listTitle: 'Allowed sites',
          emptyText: 'No sites are allowed globally',
          value: currentTypedWhitelist,
          onChange: updateCurrentTypedWhitelist,
          onAdd: addToWhitelist,
          error: errorWhitelist,
          domains: realDomains(whitelist),
          onRemove: removeFromWhitelist,
        })}
      </div>
    </section>
  );
};
